import { useState, useRef, useCallback } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, X, Loader2, UserPlus, RefreshCw, Ban } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useEnqueueJob, useJobSubscription } from "@/hooks/useJobQueue";
import { parse as parseDate, isValid, format as formatDate } from "date-fns";

// Lazy-loaded so the library only ships to users who click Bulk Upload.
let xlsxPromise: Promise<typeof import("xlsx")> | null = null;
function loadXLSX() {
  if (!xlsxPromise) xlsxPromise = import("xlsx");
  return xlsxPromise;
}

export interface BulkUploadColumn {
  key: string;
  label: string;
  required?: boolean;
  aliases?: string[];
}

export interface BulkUploadConfig {
  title: string;
  description: string;
  module: string;
  columns: BulkUploadColumn[];
  templateFileName: string;
  templateContent: string;
  /**
   * Optional: returns a Blob to download instead of the simple CSV.
   * Use for templates that need multiple sheets, formulas, or data
   * validation (e.g. attendance with employee_id dropdown). When set,
   * `templateFileName` should end in .xlsx; otherwise the CSV default
   * is used and the filename stays .csv.
   */
  templateBuilder?: () => Promise<{ blob: Blob; filename: string }>;
  onUpload: (
    rows: Record<string, string>[],
    onProgress?: (processed: number, total: number, label?: string) => void,
    signal?: AbortSignal,
  ) => Promise<{
    success: number;
    errors: string[];
    warnings?: string[];
    created?: number;
    updated?: number;
    cancelled?: boolean;
    failedRows?: Array<{ rowIndex: number; data: Record<string, string>; error: string }>;
  }>;
  /**
   * Optional check run before upload.
   * - null → no warning; proceed immediately.
   * - { canOverride: true } → show destructive "Yes, overwrite" confirmation.
   * - { canOverride: false } → show non-dismissable info banner; upload is blocked.
   */
  existingRecordCheck?: () => Promise<{ message: string; canOverride: boolean } | null>;
  /**
   * Optional strict-match validator for the parsed header row.
   * Receives normalised header strings; returns a list of error messages.
   * Non-empty list aborts parsing and displays the errors to the user.
   */
  validateHeaders?: (rawHeaders: string[]) => string[];
  /**
   * Optional cross-field row validator run at PARSE time. Receives the
   * key-mapped row + zero-based index; returns error message strings.
   * Errors land in the dialog's preview UI before the user clicks Upload,
   * so failed rows show up immediately on file select, not after upload.
   */
  validateRow?: (row: Record<string, string>, idx: number) => string[];
}

interface ParsedRow {
  data: Record<string, string>;
  errors: string[];
  errorKeys: string[]; // config column keys that failed validation
  rowIndex: number;
}

function parseCSV(text: string): string[][] {
  // Handle all line ending formats: \r\n (Windows), \n (Unix), \r (old Mac)
  const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim());
  return lines.map((line) => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') { inQuotes = !inQuotes; }
      else if (char === "," && !inQuotes) { result.push(current.trim()); current = ""; }
      else { current += char; }
    }
    result.push(current.trim());
    return result;
  });
}

/**
 * Safety fallback: convert Excel date serial numbers (e.g. 46023) to YYYY-MM-DD strings.
 * Excel stores dates as days since 1900-01-01 (with the erroneous 1900 leap-year offset).
 * This handles edge cases where cellDates/raw:false don't fully resolve the conversion.
 */
const DATE_FORMATS = [
  "yyyy-MM-dd", "yyyy-M-d",
  "yyyy/MM/dd", "yyyy/M/d",
  "dd-MM-yyyy", "d-M-yyyy",
  "dd/MM/yyyy", "d/M/yyyy",
  "dd.MM.yyyy", "d.M.yyyy",
];

function maybeConvertExcelSerial(value: string): string {
  const v = String(value ?? "").trim();
  if (!v) return v;

  const num = Number(v);
  if (!isNaN(num) && num > 36526 && num < 73050 && String(num) === v) {
    const d = new Date((num - 25569) * 86400 * 1000);
    if (isValid(d)) return formatDate(d, "yyyy-MM-dd");
  }

  for (const fmt of DATE_FORMATS) {
    const d = parseDate(v, fmt, new Date());
    if (isValid(d)) return formatDate(d, "yyyy-MM-dd");
  }

  return v;
}

/**
 * Convert Excel time serial (fractional day, e.g. 0.375 = 09:00) to HH:mm:ss string.
 * Also normalizes datetime strings to extract just the time portion.
 */
function maybeConvertExcelTime(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  // Already looks like a time (HH:mm or HH:mm:ss) — return as-is
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) return trimmed;

  // Excel time serial: fractional number (e.g. 0.375 = 09:00, or datetime serial like 45689.375)
  const num = Number(trimmed);
  if (!isNaN(num) && trimmed.includes(".")) {
    // Extract fractional part (time portion) from any Excel serial
    const fraction = num % 1;
    if (fraction > 0) {
      const totalMinutes = Math.round(fraction * 24 * 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
    }
  }

  // Pure integer that could be Excel serial without time — skip
  if (!isNaN(num) && !trimmed.includes(".") && !trimmed.includes(":")) return trimmed;

  // DateTime string like "1899-12-30T09:00:00.000Z" or "2026-02-01 09:00:00" — extract time
  const dtMatch = trimmed.match(/(\d{1,2}:\d{2}(:\d{2})?)/);
  if (dtMatch) return dtMatch[1].length === 5 ? dtMatch[1] + ":00" : dtMatch[1];

  return trimmed;
}

// Column-key tokens that flag a field as a date.
// Word-anchored so "gender" doesn't match "end" → "Gender must be a date" false positive.
const DATE_KEY_REGEX = /(^|_)(date|dob|birth|expiry|joining|leaving|start|end)(_|$)/;

function extractCellValue(val: unknown): string {
  if (val == null) return "";
  if (val instanceof Date) {
    const y = val.getFullYear();
    if (y <= 1900) {
      const hh = String(val.getHours()).padStart(2, "0");
      const mm = String(val.getMinutes()).padStart(2, "0");
      const ss = String(val.getSeconds()).padStart(2, "0");
      return `${hh}:${mm}:${ss}`;
    }
    // xlsx cellDates can return dates with a slight time-of-day offset
    // (e.g. Excel "Dec 1, 2023" → `2023-11-30T18:29:50Z`) due to how it
    // round-trips Excel serials through the local timezone. Snap to the
    // nearest UTC day so the JS date matches what Excel displays.
    const snapped = new Date(Math.round(val.getTime() / 86400000) * 86400000);
    const sy = snapped.getUTCFullYear();
    const sm = String(snapped.getUTCMonth() + 1).padStart(2, "0");
    const sd = String(snapped.getUTCDate()).padStart(2, "0");
    return `${sy}-${sm}-${sd}`;
  }
  if (typeof val === "object") {
    const o = val as { text?: unknown; result?: unknown };
    if ("text" in o) return String(o.text ?? "");
    if ("result" in o) return o.result == null ? "" : String(o.result);
  }
  return String(val);
}

export function BulkUploadDialog({ config, label = "Bulk Upload" }: { config: BulkUploadConfig; label?: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const enqueueJob = useEnqueueJob();
  const [open, setOpen] = useState(false);
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; header: string } | null>(null);
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [uploadSummary, setUploadSummary] = useState<{
    success: number; errors: string[]; warnings?: string[]; created?: number; updated?: number;
    cancelled?: boolean;
    failedRows?: Array<{ rowIndex: number; data: Record<string, string>; error: string }>;
  } | null>(null);
  const [pendingWarning, setPendingWarning] = useState<{ message: string; canOverride: boolean } | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const { job: activeJob } = useJobSubscription(activeJobId);
  const [liveProgress, setLiveProgress] = useState<{ processed: number; total: number } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setParsedRows([]);
    setHeaders([]);
    setFileName("");
    setUploadSummary(null);
    setPendingWarning(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      let blob: Blob;
      let filename: string;
      if (config.templateBuilder) {
        // Rich builder path (e.g. attendance XLSX with employee_id dropdown +
        // employee-reference sheet). Falls through to CSV on failure.
        const built = await config.templateBuilder();
        blob = built.blob;
        filename = built.filename;
      } else {
        // Plain CSV fallback. BOM so Excel detects UTF-8.
        blob = new Blob(["﻿" + config.templateContent], {
          type: "text/csv;charset=utf-8",
        });
        filename = config.templateFileName;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e: unknown) {
      toast.error(`Template download failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const validateRow = useCallback((row: Record<string, string>, idx: number): ParsedRow => {
    const errors: string[] = [];
    const errorKeys: string[] = [];
    for (const col of config.columns) {
      const value = (row[col.key] ?? "").trim();
      if (col.required && !value) {
        errors.push(`${col.label} is required`);
        errorKeys.push(col.key);
        continue;
      }
      if (value && DATE_KEY_REGEX.test(col.key)) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          errors.push(`${col.label} must be a date (got "${value}"). Accepted: YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY, or an Excel-formatted date cell.`);
          errorKeys.push(col.key);
        }
      }
    }
    if (config.validateRow) {
      for (const msg of config.validateRow(row, idx)) errors.push(msg);
    }
    return { data: row, errors, errorKeys, rowIndex: idx + 1 };
  }, [config.columns, config.validateRow]);

  const handleCellEdit = useCallback((rowIndex: number, header: string, value: string) => {
    setParsedRows(prev => prev.map(row => {
      if (row.rowIndex !== rowIndex) return row;
      const newData = { ...row.data, [header]: value };
      // If editing an aliased column, also update the primary config key
      for (const col of config.columns) {
        if (col.aliases?.includes(header)) newData[col.key] = value;
      }
      return validateRow(newData, row.rowIndex - 1);
    }));
    setEditingCell(null);
  }, [config.columns, validateRow]);

  const processRows = useCallback((rawRows: string[][]) => {
    // Filter out completely empty rows (all cells empty)
    const nonEmptyRows = rawRows.filter((row) => row.some((cell) => String(cell ?? "").trim() !== ""));
    
    if (nonEmptyRows.length < 2) {
      toast.error("File must have a header row and at least one data row");
      return;
    }
    const fileHeaders = nonEmptyRows[0].map((h) =>
      String(h ?? "")
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "")
    );
    setHeaders(fileHeaders);
    const dataRows = nonEmptyRows.slice(1).map((cells, idx) => {
      const row: Record<string, string> = {};
      fileHeaders.forEach((h, i) => {
        const val = String(cells[i] ?? "").trim();
        // Apply appropriate conversion based on column type
        const isDateColumn = DATE_KEY_REGEX.test(h);
        const isTimeColumn = /check_in|check_out|time_in|time_out|clock_in|clock_out|in_time|out_time/.test(h);
        if (isTimeColumn) {
          row[h] = maybeConvertExcelTime(val);
        } else if (isDateColumn) {
          row[h] = maybeConvertExcelSerial(val);
        } else {
          row[h] = val;
        }
      });
      // Remap aliased column names to primary config keys. Also try the
      // normalized label (e.g. "Basic Salary" → "basic_salary") because that's
      // what users see in the downloaded template, and they shouldn't have to
      // hand-author the underscore key. Same normalization as fileHeaders above.
      const normalizeHeader = (s: string) => s
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
      for (const col of config.columns) {
        if (row[col.key]) continue;
        const candidates = [
          ...(col.aliases || []),
          normalizeHeader(col.label),
        ];
        for (const cand of candidates) {
          if (row[cand]) {
            row[col.key] = row[cand];
            break;
          }
        }
      }
      return validateRow(row, idx);
    });
    setParsedRows(dataRows);
  }, [validateRow, config.columns]);

  const processFile = useCallback((file: File) => {
    // Allowlist both extension and MIME type to prevent polyglot file attacks
    const ext = file.name.split(".").pop()?.toLowerCase();
    const ALLOWED_EXTENSIONS = ["csv", "xlsx", "xls"];
    const ALLOWED_MIME_TYPES = [
      "text/csv",
      "text/plain",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/csv",
      "application/octet-stream", // some browsers report xlsx as this
    ];
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

    if (!ALLOWED_EXTENSIONS.includes(ext || "")) {
      toast.error("Please upload a CSV or Excel (.xlsx/.xls) file");
      return;
    }
    if (file.type && !ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error("File type not allowed. Please upload a CSV or Excel file.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error("File is too large. Maximum allowed size is 5 MB.");
      return;
    }
    setFileName(file.name);
    if (ext === "csv") {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target?.result as string;
        const parsed = parseCSV(text);
        if (config.validateHeaders) {
          const rawHeaders = (parsed[0] || []).map((h) => String(h ?? "").trim());
          const headerErrors = config.validateHeaders(rawHeaders);
          if (headerErrors.length > 0) {
            for (const msg of headerErrors) toast.error(msg);
            setFileName("");
            return;
          }
        }
        processRows(parsed);
      };
      reader.readAsText(file);
    } else {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const data = ev.target?.result as ArrayBuffer;
          const XLSX = await loadXLSX();
          const wb = XLSX.read(data, { type: "array", cellDates: true });

          const normalize = (s: string) => s.toLowerCase().replace(/[\s_-]+/g, "");
          const knownTokens = new Set<string>();
          for (const c of config.columns) {
            knownTokens.add(normalize(c.key));
            knownTokens.add(normalize(c.label));
            for (const a of (c.aliases || [])) knownTokens.add(normalize(a));
          }
          let bestSheetName = wb.SheetNames[0];
          let bestScore = -1;
          for (const name of wb.SheetNames) {
            const aoa = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[name], { header: 1, raw: true, defval: "" });
            const headers = (aoa[0] || []).map((h) => normalize(String(h ?? "")));
            const score = headers.reduce((acc, h) => acc + (h && knownTokens.has(h) ? 1 : 0), 0);
            if (score > bestScore) { bestScore = score; bestSheetName = name; }
          }

          const aoa = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[bestSheetName], {
            header: 1,
            raw: true,
            defval: "",
          });
          const rows: string[][] = [];
          for (const r of aoa) {
            const cells = (r as unknown[]).map(extractCellValue);
            if (cells.some((c) => c.trim() !== "")) rows.push(cells);
          }
          if (config.validateHeaders) {
            const rawHeaders = (rows[0] || []).map((h) => String(h ?? "").trim());
            const headerErrors = config.validateHeaders(rawHeaders);
            if (headerErrors.length > 0) {
              for (const msg of headerErrors) toast.error(msg);
              setFileName("");
              return;
            }
          }
          processRows(rows);
        } catch (e) {
          console.error("[BulkUpload] XLSX parse threw:", e);
          toast.error(`Failed to parse Excel file: ${e instanceof Error ? e.message : String(e)}`);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }, [processRows]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const errorCount = parsedRows.filter((r) => r.errors.length > 0).length;
  const validCount = parsedRows.length - errorCount;

  const executeUpload = async (overrideRows?: Record<string, string>[]) => {
    setUploading(true);
    setPendingWarning(null);
    setLiveProgress(null);

    const controller = new AbortController();
    abortRef.current = controller;

    const validRows = overrideRows
      ?? parsedRows.filter((r) => r.errors.length === 0).map((r) => r.data);
    setLiveProgress({ processed: 0, total: validRows.length });

    // Enqueue a job_queue row for audit + Realtime progress tracking.
    let jobId: string | null = null;
    try {
      jobId = await enqueueJob("bulk_upload", {
        module: config.module,
        file_name: fileName,
        total_rows: validRows.length,
      });
      setActiveJobId(jobId);
      void supabase
        .from("job_queue")
        .update({ status: "running", progress: 5, progress_label: "Processing rows…" })
        .eq("id", jobId)
        .then(undefined, () => {});
    } catch {
      // Job queue unavailable — continue without progress tracking
    }

    try {
      let result: {
        success: number; errors: string[]; warnings?: string[]; created?: number; updated?: number;
        cancelled?: boolean;
        failedRows?: Array<{ rowIndex: number; data: Record<string, string>; error: string }>;
      };

      if (jobId) {
        void (supabase.from("job_queue") as any)
          .update({ progress: 30, progress_label: "Uploading rows…" })
          .eq("id", jobId).then(undefined, () => {});
      }

      try {
        let lastEmit = 0;
        const onProgress = (processed: number, total: number, label?: string) => {
          setLiveProgress({ processed, total });
          if (!jobId || total <= 0) return;
          const now = Date.now();
          if (now - lastEmit < 400 && processed < total) return;
          lastEmit = now;
          const pct = Math.min(95, Math.max(30, Math.round(30 + (processed / total) * 65)));
          (supabase.from("job_queue") as any)
            .update({
              progress: pct,
              progress_label: label ?? `Processing ${processed}/${total} rows…`,
            })
            .eq("id", jobId)
            .then(undefined, () => {});
        };
        result = await config.onUpload(validRows, onProgress, controller.signal);
      } catch (uploadErr: any) {
        if (controller.signal.aborted) {
          result = { success: 0, errors: ["Upload cancelled by user. All changes rolled back."], cancelled: true };
        } else {
          console.error("[BulkUpload] onUpload threw:", uploadErr);
          result = { success: 0, errors: [uploadErr.message || "Upload failed"] };
        }
      }

      // Update job to terminal state
      if (jobId) {
        const cancelled = result.cancelled === true;
        const failed = !cancelled && result.errors.length > 0 && result.success === 0;
        await supabase.from("job_queue").update({
          status: cancelled ? "cancelled" : failed ? "failed" : "completed",
          progress: 100,
          progress_label: cancelled ? "Cancelled" : failed ? "Failed" : `${result.success} rows uploaded`,
          result: { success: result.success, errors: result.errors.slice(0, 20) },
          error_message: cancelled ? "Cancelled by user" : failed ? result.errors[0] ?? "Upload failed" : null,
        }).eq("id", jobId);
      }

      if (user) {
        try {
          const { data: profile } = await supabase
            .from("profiles")
            .select("organization_id")
            .eq("user_id", user.id)
            .maybeSingle();

          const orgId = profile?.organization_id || "00000000-0000-0000-0000-000000000001";

          const { error: historyErr } = await supabase.from("bulk_upload_history").insert({
            module: config.module,
            file_name: fileName,
            total_rows: validRows.length,
            successful_rows: result.success,
            failed_rows: result.errors.length + errorCount,
            errors: result.errors.slice(0, 50),
            uploaded_by: user.id,
            organization_id: orgId,
          });
          if (historyErr) {
            console.error("[BulkUpload] Failed to log upload history:", historyErr.message, historyErr);
            toast.warning("Upload succeeded but history logging failed. Check permissions.");
          }
        } catch (histErr: any) {
          console.error("[BulkUpload] History insert exception:", histErr);
          toast.warning("Upload succeeded but history logging failed.");
        }
        qc.invalidateQueries({ queryKey: ["bulk-upload-history"] });
      }

      setUploadSummary(result);

      const warnCount = result.warnings?.length ?? 0;
      if (result.cancelled) {
        toast.warning("Upload cancelled. All changes rolled back.");
      } else if (result.success === 0 && result.errors.length > 0) {
        toast.error(`Upload failed: ${result.errors.length} error(s)`);
      } else if (result.errors.length > 0 && warnCount > 0) {
        toast.warning(`Uploaded ${result.success} rows — ${result.errors.length} failed, ${warnCount} saved with partial data`);
      } else if (result.errors.length > 0) {
        toast.warning(`Uploaded ${result.success} rows with ${result.errors.length} failure(s)`);
      } else if (warnCount > 0) {
        toast.success(`Uploaded ${result.success} rows — ${warnCount} saved with partial data`);
      } else {
        toast.success(`Successfully uploaded ${result.success} rows`);
      }
    } catch (err: any) {
      if (jobId) {
        await (supabase.from("job_queue") as any).update({
          status: "failed", progress: 0,
          error_message: (err as Error).message || "Unexpected error",
        }).eq("id", jobId).then(undefined, () => {});
      }
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      setActiveJobId(null);
      setLiveProgress(null);
      abortRef.current = null;
    }
  };

  const handleCancel = async () => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    if (activeJobId) {
      await (supabase.from("job_queue") as any)
        .update({ status: "cancelled", progress: 100, progress_label: "Cancelled", error_message: "Cancelled by user" })
        .eq("id", activeJobId)
        .then(undefined, () => {});
    }
    toast.warning("Cancelling upload — rolling back changes…");
  };

  const handleRetryFailed = async () => {
    if (!uploadSummary?.failedRows?.length) return;
    const rows = uploadSummary.failedRows.map((f) => f.data);
    setUploadSummary(null);
    await executeUpload(rows);
  };

  const handleUpload = async () => {
    if (validCount === 0) {
      toast.error("No valid rows to upload");
      return;
    }

    // Flip button to "Uploading…" immediately so the click registers visually,
    // even before the (potentially slow) existingRecordCheck network roundtrip.
    setUploading(true);

    // If a pre-upload check is configured, run it and gate on user confirmation.
    // Wrap in try/catch — handleUpload is awaited from onClick, and any
    // unhandled rejection here would otherwise vanish silently (the user just
    // sees the button do nothing).
    if (config.existingRecordCheck) {
      console.log("[BulkUpload] running existingRecordCheck…");
      const TIMEOUT_SENTINEL = Symbol("timeout");
      try {
        // 25s soft ceiling — pre-upload check is advisory; the upload path
        // re-validates server-side. On timeout we proceed rather than block.
        const warning = await Promise.race([
          config.existingRecordCheck(),
          new Promise<typeof TIMEOUT_SENTINEL>((resolve) =>
            setTimeout(() => resolve(TIMEOUT_SENTINEL), 25000)
          ),
        ]);
        if (warning === TIMEOUT_SENTINEL) {
          console.warn("[BulkUpload] existingRecordCheck timed out — proceeding without warning banner");
          toast.warning("Skipped duplicate-data check (slow connection). Proceeding with upload.");
        } else if (warning) {
          console.log("[BulkUpload] existingRecordCheck result:", warning);
          setPendingWarning(warning);
          if (warning.canOverride) {
            toast.warning("Existing data — confirm overwrite in the dialog");
          } else {
            toast.error(warning.message);
          }
          setUploading(false);
          return;
        }
      } catch (err: any) {
        // Non-fatal: log and continue. Upload path enforces final validation.
        console.error("[BulkUpload] existingRecordCheck threw — proceeding anyway:", err);
        toast.warning("Couldn't pre-check existing data. Proceeding with upload.");
      }
    }

    console.log("[BulkUpload] starting executeUpload (validRows:", validCount, ")");

    try {
      await executeUpload();
    } catch (err: any) {
      console.error("[BulkUpload] executeUpload threw:", err);
      toast.error(`Upload failed: ${err?.message ?? "Unknown error"}`);
      setUploading(false);
      setActiveJobId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            {config.title}
          </DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-hidden">
          {/* Upload summary — shown after upload completes */}
          {uploadSummary && (
            <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-3">
              <div className="flex items-center gap-2">
                {uploadSummary.cancelled ? (
                  <>
                    <Ban className="h-5 w-5 text-warning" />
                    <p className="font-semibold text-sm">Upload Cancelled — All Changes Rolled Back</p>
                  </>
                ) : uploadSummary.success === 0 && uploadSummary.errors.length > 0 ? (
                  <>
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                    <p className="font-semibold text-sm">Upload Failed — No Rows Were Saved</p>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-primary" />
                    <p className="font-semibold text-sm">Upload Complete</p>
                  </>
                )}
              </div>
              <div className={cn("grid gap-3", (uploadSummary.created !== undefined || uploadSummary.updated !== undefined) ? "grid-cols-3" : "grid-cols-1")}>
                <div className="rounded-md border border-border bg-background p-3 text-center">
                  <p className="text-2xl font-bold text-foreground">{uploadSummary.success}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Total Processed</p>
                </div>
                {uploadSummary.created !== undefined && (
                  <div className="rounded-md border border-border bg-background p-3 text-center">
                    <div className="flex items-center justify-center gap-1.5 mb-0.5">
                      <UserPlus className="h-4 w-4 text-primary" />
                      <p className="text-2xl font-bold text-foreground">{uploadSummary.created}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">New Users Created</p>
                  </div>
                )}
                {uploadSummary.updated !== undefined && (
                  <div className="rounded-md border border-border bg-background p-3 text-center">
                    <div className="flex items-center justify-center gap-1.5 mb-0.5">
                      <RefreshCw className="h-4 w-4 text-primary" />
                      <p className="text-2xl font-bold text-foreground">{uploadSummary.updated}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Existing Updated</p>
                  </div>
                )}
              </div>
              {uploadSummary.errors.length > 0 && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-1">
                  <p className="text-xs font-medium text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {uploadSummary.errors.length} row{uploadSummary.errors.length > 1 ? "s" : ""} failed
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto">
                    {uploadSummary.errors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                </div>
              )}
              {(uploadSummary.warnings ?? []).length > 0 && (
                <div className="rounded-md border border-warning/30 bg-warning/5 p-3 space-y-1">
                  <p className="text-xs font-medium text-warning flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {uploadSummary.warnings!.length} row{uploadSummary.warnings!.length > 1 ? "s" : ""} saved with partial data
                  </p>
                  <ul className="text-xs text-muted-foreground space-y-0.5 max-h-24 overflow-y-auto">
                    {uploadSummary.warnings!.map((w, i) => <li key={i}>• {w}</li>)}
                  </ul>
                </div>
              )}
              {(uploadSummary.failedRows?.length ?? 0) > 0 && !uploadSummary.cancelled && (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-destructive flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {uploadSummary.failedRows!.length} failed row{uploadSummary.failedRows!.length > 1 ? "s" : ""} can be retried
                    </p>
                    <Button size="sm" variant="outline" onClick={handleRetryFailed} disabled={uploading}>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Retry failed rows
                    </Button>
                  </div>
                  <div className="max-h-40 overflow-y-auto rounded border bg-background">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[10px] h-7">Row</TableHead>
                          <TableHead className="text-[10px] h-7">Identifier</TableHead>
                          <TableHead className="text-[10px] h-7">Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadSummary.failedRows!.map((f, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-[11px] py-1">{f.rowIndex}</TableCell>
                            <TableCell className="text-[11px] py-1 truncate max-w-[140px]">
                              {f.data.employee_id || f.data.email_id || f.data.email || f.data.name || "—"}
                            </TableCell>
                            <TableCell className="text-[11px] py-1 text-destructive">{f.error}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Upload form — hidden after upload */}
          {!uploadSummary && (
            <>
              {/* Step 1: Download template */}
              <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-4">
                <div>
                  <p className="font-medium text-sm">Step 1: Download the Excel template</p>
                  <p className="text-xs text-muted-foreground">Fill in the template with your data, then upload it below</p>
                </div>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
              </div>

              {/* Step 2: Upload file */}
              <div
                className={cn(
                  "rounded-lg border-2 border-dashed p-6 transition-colors text-center",
                  isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"
                )}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} className="hidden" />
                <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="font-medium text-sm mb-1">
                  {isDragging ? "Drop your file here" : "Step 2: Drag & drop or choose a file"}
                </p>
                <p className="text-xs text-muted-foreground mb-3">Supports CSV, Excel (.xlsx, .xls)</p>
                <div className="flex items-center justify-center gap-3">
                  <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()}>
                    Choose File
                  </Button>
                  {fileName && (
                    <div className="flex items-center gap-2 text-sm">
                      <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                      <span>{fileName}</span>
                      <Button variant="ghost" size="icon" className="h-5 w-5" onClick={reset}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview table */}
              {parsedRows.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <p className="font-medium text-sm">Preview ({parsedRows.length} rows)</p>
                    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {validCount} valid
                    </Badge>
                    {errorCount > 0 && (
                      <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {errorCount} errors
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Click any cell to edit its value inline.</p>
                  <div className="h-[220px] overflow-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8 sticky left-0 bg-background z-10">#</TableHead>
                          {headers.map((h) => {
                            const isMapped = config.columns.some(c => c.key === h || c.aliases?.includes(h));
                            return (
                              <TableHead key={h} className={cn("text-xs whitespace-nowrap", !isMapped && "text-muted-foreground/60")}>
                                {h}{!isMapped && <span className="ml-1 text-[10px] font-normal opacity-60">(ignored)</span>}
                              </TableHead>
                            );
                          })}
                          <TableHead className="text-xs whitespace-nowrap min-w-[160px]">Status / Errors</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {parsedRows.map((row) => (
                          <TableRow key={row.rowIndex} className={row.errors.length > 0 ? "bg-destructive/5" : ""}>
                            <TableCell className="text-xs text-muted-foreground sticky left-0 bg-inherit">{row.rowIndex}</TableCell>
                            {headers.map((h) => {
                              const configCol = config.columns.find(c => c.key === h || c.aliases?.includes(h));
                              const isErrorCell = !!configCol && row.errorKeys.includes(configCol.key);
                              const isEditing = editingCell?.rowIndex === row.rowIndex && editingCell?.header === h;
                              return (
                                <TableCell
                                  key={h}
                                  className={cn("text-xs p-1 cursor-pointer", isErrorCell ? "bg-destructive/15" : "")}
                                  onClick={() => { if (!isEditing) setEditingCell({ rowIndex: row.rowIndex, header: h }); }}
                                >
                                  {isEditing ? (
                                    <input
                                      autoFocus
                                      defaultValue={row.data[h] ?? ""}
                                      className="w-full min-w-[80px] px-1 py-0.5 rounded border border-primary bg-background text-xs outline-none"
                                      onBlur={(e) => handleCellEdit(row.rowIndex, h, e.target.value.trim())}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleCellEdit(row.rowIndex, h, (e.target as HTMLInputElement).value.trim());
                                        if (e.key === "Escape") setEditingCell(null);
                                      }}
                                    />
                                  ) : (
                                    <span
                                      className={cn("block max-w-[120px] truncate", isErrorCell ? "text-destructive font-medium italic" : "")}
                                      title={isErrorCell ? `${configCol?.label} is required — click to edit` : (row.data[h] || "")}
                                    >
                                      {row.data[h] || (isErrorCell ? "missing" : "-")}
                                    </span>
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-xs min-w-[160px]">
                              {row.errors.length > 0 ? (
                                <div className="space-y-0.5">
                                  {row.errors.map((e, i) => (
                                    <div key={i} className="flex items-start gap-1 text-destructive leading-tight">
                                      <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                                      <span>{e}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <CheckCircle2 className="h-4 w-4 text-primary" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Pre-upload check banner */}
        {pendingWarning && (
          <div className={cn(
            "shrink-0 mx-6 mb-2 rounded-lg border p-4 space-y-3",
            pendingWarning.canOverride
              ? "border-destructive/60 bg-destructive/10"
              : "border-warning/60 bg-warning/10"
          )}>
            <div className="flex items-start gap-2">
              <AlertTriangle className={cn("h-5 w-5 shrink-0 mt-0.5", pendingWarning.canOverride ? "text-destructive" : "text-warning")} />
              <div className="space-y-1">
                <p className={cn("text-sm font-semibold", pendingWarning.canOverride ? "text-destructive" : "text-warning")}>
                  {pendingWarning.canOverride ? "Overwrite existing data?" : "Upload blocked"}
                </p>
                <p className="text-sm text-muted-foreground">{pendingWarning.message}</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setPendingWarning(null)}>
                {pendingWarning.canOverride ? "Cancel" : "Got it"}
              </Button>
              {pendingWarning.canOverride && (
                <Button variant="destructive" size="sm" onClick={() => executeUpload()} disabled={uploading}>
                  {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : "Yes, overwrite"}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Progress bar — visible while a job is active */}
        {uploading && (() => {
          const livePct = liveProgress && liveProgress.total > 0
            ? Math.round((liveProgress.processed / liveProgress.total) * 100)
            : null;
          const pct = livePct ?? activeJob?.progress ?? 5;
          return (
            <div className="px-6 pb-2 space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{activeJob?.progress_label ?? "Processing…"}</span>
                <span>
                  {liveProgress
                    ? `${liveProgress.processed} / ${liveProgress.total} rows · ${pct}%`
                    : `${pct}%`}
                </span>
              </div>
              <Progress
                value={pct}
                className={cn("h-2", activeJob?.status === "running" && pct < 100 && "animate-pulse")}
              />
            </div>
          );
        })()}

        <DialogFooter>
          {uploadSummary ? (
            <Button onClick={() => { reset(); setOpen(false); }}>Done</Button>
          ) : uploading ? (
            <>
              <Button
                variant="destructive"
                onClick={handleCancel}
                disabled={!!abortRef.current?.signal.aborted}
              >
                <Ban className="h-4 w-4 mr-2" />
                {abortRef.current?.signal.aborted ? "Cancelling…" : "Cancel upload"}
              </Button>
              <Button disabled>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button onClick={handleUpload} disabled={validCount === 0 || !!pendingWarning}>
                <Upload className="h-4 w-4 mr-2" />Upload {validCount} Rows
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
